import { Fragment, useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useFeatureFlag, useToggleFeatureFlag } from "@/hooks/useFeatureFlag";
import { useLinkedinImportLogs } from "@/hooks/useLinkedinImportLogs";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { RefreshCw, Play, Power, AlertTriangle, Clock, Download } from "lucide-react";

const FLAG = "linkedin_import_enabled";

function formatDuration(ms: number | null) {
  if (!ms && ms !== 0) return "-";
  if (ms < 1000) return `${ms}ms`;
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(1)}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${Math.round(s % 60)}s`;
}

function statusBadge(status: string) {
  const map: Record<string, { label: string; className: string }> = {
    success: { label: "Sucesso", className: "bg-emerald-500/15 text-emerald-600 border-emerald-500/30" },
    partial: { label: "Parcial", className: "bg-amber-500/15 text-amber-600 border-amber-500/30" },
    error: { label: "Erro", className: "bg-destructive/15 text-destructive border-destructive/30" },
    skipped: { label: "Ignorado", className: "bg-muted text-muted-foreground border-border" },
  };
  const cfg = map[status] ?? { label: status, className: "bg-muted text-muted-foreground" };
  return <Badge variant="outline" className={cfg.className}>{cfg.label}</Badge>;
}

export function LinkedinImportPanel() {
  const { isEnabled, updatedAt, isLoading: flagLoading } = useFeatureFlag(FLAG);
  const toggle = useToggleFeatureFlag(FLAG);
  const { data: logs = [], isLoading, refetch, isFetching } = useLinkedinImportLogs(50);
  const [running, setRunning] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);

  const handleToggle = async (next: boolean) => {
    try {
      await toggle.mutateAsync(next);
      toast({
        title: next ? "Importação automática ativada" : "Importação automática pausada",
        description: next
          ? "O robô voltará a rodar no próximo agendamento."
          : "As execuções agendadas serão ignoradas até você reativar.",
      });
    } catch (e: any) {
      toast({ title: "Erro ao atualizar", description: e.message, variant: "destructive" });
    }
  };

  const handleRunNow = async () => {
    setRunning(true);
    try {
      const { data, error } = await supabase.functions.invoke("buscar-vagas-linkedin", { body: {} });
      if (error) throw error;
      if (data?.skipped) {
        toast({
          title: "Execução ignorada",
          description: "A importação está pausada. Ative o interruptor para rodar.",
        });
      } else {
        toast({
          title: "Importação concluída",
          description: `Buscadas: ${data?.buscadas ?? 0} · Duplicadas: ${data?.duplicadas ?? 0} · Inseridas: ${data?.inseridas ?? 0}`,
        });
      }
      refetch();
    } catch (e: any) {
      toast({
        title: "Falha ao executar",
        description: e.message ?? "Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setRunning(false);
    }
  };

  const last = logs[0];
  const thirtyDaysAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;
  const inserted30d = logs
    .filter((l) => new Date(l.created_at).getTime() >= thirtyDaysAgo)
    .reduce((acc, l) => acc + (l.inserted_count ?? 0), 0);
  const errorCount = logs.filter((l) => l.status === "error").length;

  return (
    <div className="space-y-6">
      <Card className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-start gap-3">
          <Power className={`h-5 w-5 mt-0.5 ${isEnabled ? "text-primary" : "text-muted-foreground"}`} />
          <div>
            <p className="font-medium text-sm">Importação automática ativa</p>
            <p className="text-xs text-muted-foreground max-w-xl">
              Agendada a cada 2 dias, às 09h de Brasília. Quando desligada, o robô não busca nada
              e registra a execução como "Ignorado" — você pode reativar quando quiser.
            </p>
            {updatedAt && (
              <p className="text-[11px] text-muted-foreground mt-1">
                Última alteração: {new Date(updatedAt).toLocaleString("pt-BR")}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button size="sm" variant="outline" onClick={handleRunNow} disabled={running}>
            <Play className={`h-4 w-4 mr-1.5 ${running ? "animate-pulse" : ""}`} />
            {running ? "Executando..." : "Executar agora"}
          </Button>
          <Switch
            checked={isEnabled}
            onCheckedChange={handleToggle}
            disabled={flagLoading || toggle.isPending}
          />
        </div>
      </Card>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className="p-4">
          <div className="flex items-center gap-2 text-muted-foreground text-xs">
            <Clock className="h-3.5 w-3.5" /> Última execução
          </div>
          <p className="text-sm font-semibold mt-1">
            {last ? new Date(last.created_at).toLocaleString("pt-BR") : "Nunca"}
          </p>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 text-muted-foreground text-xs">
            <RefreshCw className="h-3.5 w-3.5" /> Execuções registradas
          </div>
          <p className="text-2xl font-bold mt-1">{logs.length}</p>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 text-muted-foreground text-xs">
            <Download className="h-3.5 w-3.5" /> Vagas inseridas (30d)
          </div>
          <p className="text-2xl font-bold mt-1">{inserted30d}</p>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 text-muted-foreground text-xs">
            <AlertTriangle className="h-3.5 w-3.5" /> Execuções com erro
          </div>
          <p className="text-2xl font-bold mt-1">{errorCount}</p>
        </Card>
      </div>

      <Card className="p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold">Histórico de execuções</h2>
          <Button size="sm" variant="ghost" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw className={`h-4 w-4 mr-1.5 ${isFetching ? "animate-spin" : ""}`} />
            Atualizar
          </Button>
        </div>

        {isLoading ? (
          <p className="text-sm text-muted-foreground">Carregando...</p>
        ) : logs.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nenhuma execução registrada ainda.</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data</TableHead>
                  <TableHead>Origem</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Buscadas</TableHead>
                  <TableHead className="text-right">Duplicadas</TableHead>
                  <TableHead className="text-right">Inseridas</TableHead>
                  <TableHead className="text-right">Duração</TableHead>
                  <TableHead>Mensagem</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map((log) => {
                  const errorList = Array.isArray(log.errors) ? (log.errors as string[]) : [];
                  const isOpen = expanded === log.id;
                  return (
                    <Fragment key={log.id}>
                      <TableRow

                        className={errorList.length > 0 ? "cursor-pointer" : undefined}
                        onClick={() => errorList.length > 0 && setExpanded(isOpen ? null : log.id)}
                      >
                        <TableCell className="whitespace-nowrap text-xs">
                          {new Date(log.created_at).toLocaleString("pt-BR")}
                        </TableCell>
                        <TableCell className="text-xs capitalize">{log.triggered_by}</TableCell>
                        <TableCell>{statusBadge(log.status)}</TableCell>
                        <TableCell className="text-right text-xs">{log.fetched_count}</TableCell>
                        <TableCell className="text-right text-xs">{log.duplicated_count}</TableCell>
                        <TableCell className="text-right text-xs font-medium">{log.inserted_count}</TableCell>
                        <TableCell className="text-right text-xs">{formatDuration(log.duration_ms)}</TableCell>
                        <TableCell className="text-xs max-w-[280px] truncate text-muted-foreground">
                          {log.error_message ||
                            (errorList.length > 0 ? `${errorList.length} aviso(s) — clique para ver` : "-")}
                        </TableCell>
                      </TableRow>
                      {isOpen && (
                        <TableRow key={`${log.id}-details`}>
                          <TableCell colSpan={8} className="bg-muted/40">
                            <ul className="text-xs space-y-1 list-disc pl-5">
                              {errorList.map((err, i) => (
                                <li key={i} className="text-muted-foreground break-all">{String(err)}</li>
                              ))}
                            </ul>
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
    </div>
  );
}
